import React, { useMemo } from "react";
import { IntlShape, useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { useHotelRoomManagement } from "shared/hooks/useHotelRoomManagement";
import { AllowedTravelerType, HotelSettings } from "shared/types/hotels";

import Img from "@components/FileServer/Img";
import Counter from "@components/SearchForm/Counter";
import FormattedMessage from "@components/UI/FormattedMessage";
import CrossIcon from "@components/UI/Icons/Cross";
import SimpleSelect from "@components/UI/SimpleSelect";

interface IHotelPassengersSelectProps {
  hotelSearchSettings: HotelSettings | null | undefined;
  allowedTravelerTypes: AllowedTravelerType[];
  selectComponentPrefix: string;
  toggleShowProperties?: () => void;
  setErrorMessage: (messageNumber: number) => void;
}

const HotelPassengerSelect: React.FC<IHotelPassengersSelectProps> = ({
  hotelSearchSettings,
  allowedTravelerTypes,
  selectComponentPrefix,
  toggleShowProperties,
  setErrorMessage,
}) => {
  const intl = useIntl();

  const searchHotelsState = useSelector(
    (state: AppState) => state.search.searchHotels
  );

  const hotelMaxRooms = parseInt(hotelSearchSettings?.RoomCount?.Max, 10) ?? 9;

  const {
    rooms,
    collapsedStates,
    maxTravelersError,
    handleAddPassenger,
    handleRemovePassenger,
    handleChildAgeChange,
    resetRoomsToDefault,
    addRoom,
    removeRoom,
    toggleRoomCollapse,
    isDefaultState,
    needMoreAdultsError,
  } = useHotelRoomManagement({
    searchHotelsRooms: searchHotelsState?.rooms,
    hotelMaxRooms,
    hotelSearchSettings,
    setErrorMessage,
  });

  const travelerTypesSettings =
    hotelSearchSettings?.RoomGuestLimits?.GuestLimit;

  const childAgeOptions = useMemo(() => {
    return generateChildAgeOptions(intl);
  }, [intl]);

  if (
    !Array.isArray(travelerTypesSettings) ||
    travelerTypesSettings.length === 0
  ) {
    return <div>Loading hotel settings or no guest types defined...</div>;
  }

  const travelerNameMap = allowedTravelerTypes.reduce<Record<string, string>>(
    (acc, travelerType) => {
      acc[travelerType.Code] = travelerType.$t;
      return acc;
    },
    {}
  );

  const childSettings = travelerTypesSettings.find((tt) => tt.Code === "CHD");

  return (
    <div className="hotel-passenger-select">
      {rooms.map((room, roomIndex) => {
        const isCollapsed = collapsedStates[roomIndex];
        const adults = (room.ADT as number) || 0;
        const children = (room.CHD as number) || 0;
        const ages = (room.childAges as number[]) || [];

        return (
          <div key={`room-${roomIndex}`} className="hotel-select-container">
            <div
              className={`room-summary-row ${isCollapsed ? "collapsed" : ""}`}
              onClick={() => toggleRoomCollapse(roomIndex)}
              id={`room-details-${roomIndex}${isCollapsed ? "-collapsed" : ""}`}
              role="button"
              tabIndex={0}
              aria-expanded={!isCollapsed}
              aria-controls={`room-details-${roomIndex}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  toggleRoomCollapse(roomIndex);
                }
              }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  removeRoom(roomIndex);
                }}
                aria-label={`Remove Room ${roomIndex + 1}`}
                className="remove-room-button"
              >
                <CrossIcon size="sm" color="white" />
              </button>
              <div className="room-summary-text">
                <span
                  className={
                    needMoreAdultsError ? "room-title-error" : "room-title"
                  }
                >
                  <FormattedMessage id="SearchForm.room" /> {roomIndex + 1}:
                </span>{" "}
                {`${travelerNameMap.ADT}: ${adults}`}
                {", "}
                {`${travelerNameMap.CHD}: ${children}`}
              </div>
              <span className="collapse-icon">
                <Img
                  src="/static/images/ico-arrow-down.svg"
                  id={`${selectComponentPrefix}-properties-arrow`}
                  style={{
                    padding: "2px",
                    marginLeft: "5px",
                    transition: "all .4s ease",
                    transform: isCollapsed ? "rotate(180deg)" : null,
                  }}
                />
              </span>
            </div>

            {!isCollapsed && (
              <div id={`room-details-${roomIndex}`} className="room-details">
                <div className="passengers-config-row">
                  <div className="counters-row">
                    {travelerTypesSettings
                      .filter((tt) => tt.Code === "ADT" || tt.Code === "CHD")
                      .map((travelerType) => {
                        const travelerCode = travelerType.Code;
                        const currentValue =
                          (room[travelerCode] as number) ||
                          (travelerCode === "ADT" ? 1 : 0);
                        const displayName =
                          allowedTravelerTypes.find(
                            (att) => att.Code === travelerCode
                          )?.$t ?? travelerCode;

                        const travelerSettings = travelerTypesSettings.find(
                          (limit) => limit.Code === travelerCode
                        );
                        const minValue = travelerSettings?.Min
                          ? parseInt(travelerSettings.Min, 10)
                          : travelerCode === "ADT"
                          ? 1
                          : 0;

                        const maxOccupancy = Number(travelerSettings?.Max) ?? 9;
                        const totalOccupants = adults + children;
                        const isMaxTotalOccupancy =
                          totalOccupants >=
                          Number(hotelSearchSettings?.RoomOccupancy?.Max);

                        const infantsAge = childSettings?.MaxAge ?? -1;
                        const nonInfantChildrenCount = ages.filter(
                          (age) => Number(age) <= Number(infantsAge)
                        ).length;

                        const isChildLimitReached =
                          travelerCode === "CHD" &&
                          childSettings?.LessOrEqualToAdults === "true" &&
                          nonInfantChildrenCount >= adults &&
                          adults > 0;

                        const chdWouldBeLessThanAdults =
                          travelerCode === "ADT" &&
                          childSettings?.LessOrEqualToAdults === "true" &&
                          (() => {
                            return currentValue === nonInfantChildrenCount;
                          })();

                        const disabledMinus =
                          currentValue <= minValue || chdWouldBeLessThanAdults;
                        const disabledPlus =
                          currentValue >= maxOccupancy ||
                          isMaxTotalOccupancy ||
                          isChildLimitReached;

                        return (
                          <div
                            key={`${travelerCode}-counter-${roomIndex}`}
                            className="counter-item"
                          >
                            <span className="counter-label">
                              {displayName}:
                            </span>
                            <Counter
                              type={travelerCode}
                              value={currentValue}
                              onPlusSign={() =>
                                handleAddPassenger(roomIndex, travelerCode)
                              }
                              onMinusSign={() =>
                                handleRemovePassenger(roomIndex, travelerCode)
                              }
                              disabledMinus={disabledMinus}
                              disabledPlus={disabledPlus}
                            />
                          </div>
                        );
                      })}
                  </div>

                  {children > 0 && (
                    <div className="child-ages-row">
                      <div className="child-age-label">
                        <FormattedMessage id="SearchForm.ageOfChildren" />
                      </div>
                      <div className="child-age-selects">
                        {Array.from({ length: Math.ceil(children / 4) }).map(
                          (_, rowIdx) => (
                            <div
                              key={`age-row-${roomIndex}-${rowIdx}`}
                              className="age-selector-row"
                            >
                              {Array.from({
                                length: Math.min(4, children - rowIdx * 4),
                              }).map((unused, idx) => {
                                const childIdx = rowIdx * 4 + idx;

                                const selectedAge =
                                  ages[childIdx]?.toString() || 1;

                                return (
                                  <div
                                    key={`age-group-${roomIndex}-${childIdx}`}
                                    className="age-selector-group"
                                  >
                                    <SimpleSelect
                                      key={`child-age-${roomIndex}-${childIdx}`}
                                      customClass={`${selectComponentPrefix}-child-age-select`}
                                      options={childAgeOptions}
                                      value={childAgeOptions.find((opt) => {
                                        return (
                                          opt.value === selectedAge.toString()
                                        );
                                      })}
                                      onChange={(selectedOption) => {
                                        if (selectedOption?.value) {
                                          handleChildAgeChange(
                                            roomIndex,
                                            childIdx,
                                            selectedOption.value
                                          );
                                        }
                                      }}
                                      isSearchable={false}
                                      testId={`child-age-${roomIndex}-${childIdx}`}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
      <div className="action-buttons">
        {needMoreAdultsError && (
          <div className="error-message-container">
            <FormattedMessage
              id={needMoreAdultsError}
              values={{ maxAge: Number(childSettings?.MaxAge) + 1 }}
            />
          </div>
        )}
        {maxTravelersError && (
          <div className="error-message-container">
            <FormattedMessage id={maxTravelersError} />
          </div>
        )}
        <div className="buttons-section-wrapper">
          <div className="buttons-container">
            {rooms.length < hotelMaxRooms && (
              <button
                type="button"
                onClick={addRoom}
                id="add-room-button"
                className="button button-secondary-color"
              >
                <FormattedMessage id="SearchForm.addRoom" />
              </button>
            )}
            <button
              type="button"
              onClick={resetRoomsToDefault}
              id="reset-rooms-button"
              disabled={isDefaultState}
              className="button button-secondary-color"
            >
              {hotelMaxRooms > 1 ? (
                <FormattedMessage id="SearchForm.resetRooms" />
              ) : (
                <FormattedMessage id="SearchForm.resetRoom" />
              )}
            </button>
          </div>
          <div className="buttons-container done-button">
            <button
              type="button"
              id="done-room-button"
              title={intl.formatMessage({ id: "PropertiesSelect.done" })}
              disabled={!!needMoreAdultsError}
              onClick={toggleShowProperties}
              className="button button-secondary-color"
            >
              <FormattedMessage id="PropertiesSelect.done" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const getAgeWord = (intl: IntlShape, age: number): string => {
  return intl.formatMessage({ id: "SearchForm.age" }, { count: age });
};

export const generateChildAgeOptions = (
  intl: IntlShape
): Array<{ label: string; value: string }> => {
  const minAge = 0;
  const maxAge = 18;
  const length = maxAge - minAge + 1;

  return Array.from({ length }, (_, i) => ({
    label: getAgeWord(intl, i + minAge),
    value: (i + minAge).toString(),
  }));
};

export default HotelPassengerSelect;
